import React from 'react';
import Header from './Header';
import Footer from './Footer';
import logo from '../assets/logo.png';
// --- SVG Icons Component ---
// Custom SVG icons using path data
const SVGIcon = ({ name }) => {
    let iconPath;
    let color = 'currentColor'; // Used to inherit fill color from CSS

    switch (name) {
        case 'Scale':
            iconPath = "M12 2L2 22h20zm0 18l-8-16h16zM10 14h4v4h-4z"; // Simple Growth/Chart
            break;
        case 'Innovation':
            iconPath = "M9 21c0 1.657 1.343 3 3 3s3-1.343 3-3M3 10h18M12 2a4 4 0 014 4c0 1.846-1.127 3.425-2.68 4.093l-.32.146V14h-2v-3.761l-.32-.146C8.127 9.425 7 7.846 7 6a4 4 0 014-4z"; // Lightbulb
            break;
        case 'Trust':
            iconPath = "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"; // Checkmark in circle
            break;
        case 'Sustainability':
            iconPath = "M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.17 0 2.24.3 3.2.82l-1.46 1.46L14 12h8V4l-2.35 2.35z"; // Refresh/Recycle Arrow
            break;
        default:
            iconPath = "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"; // Default circle
    }

    return (
        <div className="icon-wrapper">
            <svg viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
                <path d={iconPath} />
            </svg>
        </div>
    );
};


// Utility component for the sparkling text effect
const SparkleText = ({ children }) => (
  <span className="sparkle-text">
    {children}
  </span>
);

// Component for a single "Why Choose Us" feature card
const FeatureCard = ({ title, description, iconName }) => (
    <li className="feature-list-item">
        <SVGIcon name={iconName} />
        <h4 style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.4rem', color: 'var(--deep-blue)' }}>{title}</h4>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-light)', lineHeight: '1.4' }}>{description}</p>
    </li>
);

// --- Main Partner Component (exported as App for execution environment compatibility) ---
export default function App() {

    // --- Data for sections ---
    const coreServices = [
        "Commercial & High-End Residential AC Installation (VRF, Chillers, Splits)",
        "Authorized Servicing Partner – Samsung, Carrier, and leading HVAC Brands",
        "Comprehensive AMC (Annual Maintenance Contracts) & Emergency Breakdown Services (24/7 Support)",
        "Precision Duct Manufacturing & Supply (Custom Galvanized and Aluminum)",
        "Dedicated Project Support Services for Architects, Builders, and Project Contractors",
        "Energy Audit and Optimization Consultation for existing HVAC infrastructure.",
    ];

    const whyChooseUsFeatures = [
        {
            title: 'Unmatched Reliability & Trust',
            description: 'Top global brands trust us due to our systematic processes, adherence to timelines, and 8+ years of proven operational integrity across India.',
            iconName: 'Trust',
        },
        {
            title: 'Rapid Scalability & Manpower',
            description: 'Our pool of 30+ highly experienced, certified technicians allows us to quickly scale teams for urgent or large-scale project deployments across multiple sites.',
            iconName: 'Scale',
        },
        {
            title: 'Innovation & Efficiency',
            description: 'We prioritize energy-efficient solutions and use advanced diagnostics to ensure systems run optimally, minimizing long-term operating costs for clients.',
            iconName: 'Innovation',
        },
        {
            title: 'Total Process Transparency',
            description: 'Receive daily updates, detailed progress reports, and clear, reverse quotations. You track our performance every step of the way with full accountability.',
            iconName: 'Trust',
        },
    ];
    
    const technologyFeatures = [
        {
            title: 'Advanced Diagnosis Tools',
            detail: 'Utilizing thermal imaging and digital refrigerant analyzers for non-invasive, accurate system health checks, reducing downtime.',
        },
        {
            title: 'Project Management Software',
            detail: 'Every project is managed via a dedicated platform for real-time tracking of labor, materials, and deadlines, ensuring projects are on schedule.',
        },
        {
            title: 'Sustainable Practices',
            detail: 'Focus on eco-friendly refrigerants and optimized system design to meet modern energy consumption targets and promote sustainability.',
        },
    ];

    const mediaAssets = [
        { 
            type: 'image', 
            src: 'uploaded:WhatsApp Image 2025-11-20 at 3.18.54 PM.jpeg-211975da-3913-4801-9352-2c00c0bbd950', 
            alt: 'Technician working on outdoor AC unit' 
        },
        { 
            type: 'image', 
            src: 'uploaded:WhatsApp Image 2025-11-20 at 1.16.30 PM.jpeg-6fd8e584-fc35-4c95-9326-8846206a8863', 
            alt: 'Technician on ladder servicing indoor AC unit' 
        },
        { 
            type: 'image', 
            src: 'uploaded:WhatsApp Image 2025-11-22 at 11.47.46 AM (1).jpeg-fae34d3d-e14f-4b6e-9fdc-deedb17fa448', 
            alt: 'Technician repairing AC unit' 
        },
        { 
            type: 'video', 
            src: 'Video Placeholder', 
            alt: 'Duct manufacturing process video' 
        },
    ];

    const handleCtaClick = () => {
        console.log("Contact button clicked! Initiating partnership inquiry.");
    };


    return (
        <>
            <style jsx="true">{`
                /* --- Root Variables & Global Reset --- */
                :root {
                    --sky-blue: #1E90FF;
                    --deep-blue: #0047AB;
                    --sky-orange: #FF4500;
                    --light-orange: #FF8C00;
                    --bg-white: #FFFFFF;
                    --shadow-light: rgba(0, 71, 171, 0.15);
                    --shadow-heavy: rgba(30, 144, 255, 0.4);
                    --text-dark: #333;
                    --text-light: #555;
                    --vfx-color: #a0d4ff; /* Light blue for VFX glow */
                }

                /* Keyframe Animations */
                @keyframes sparkle {
                    0%, 100% { text-shadow: 0 0 5px var(--sky-orange), 0 0 10px var(--sky-orange), 0 0 20px var(--light-orange); }
                    50% { text-shadow: none; }
                }
                @keyframes slide-in {
                    from { transform: translateY(30px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                @keyframes vfx-pulse {
                    0% { box-shadow: 0 0 0 0 rgba(30, 144, 255, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(30, 144, 255, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(30, 144, 255, 0); }
                }
                @keyframes rotation-subtle {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Utility Text Styling */
                .sparkle-text {
                    color: var(--sky-orange);
                    animation: sparkle 2s infinite ease-in-out;
                }

                /* === Partner With Us Page Styling === */
                .partner-page {
                    padding: 0 1rem;
                    background-color: #f7faff; 
                    font-family: 'Inter', sans-serif;
                    perspective: 1000px; /* Needed for 3D transforms */
                    padding-bottom: 3rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                .partner-header {
                    background-color: var(--deep-blue);
                    color: var(--bg-white);
                    padding: 3rem 1rem;
                    margin-bottom: 3rem;
                    text-align: center;
                    clip-path: polygon(0 0, 100% 0, 100% 90%, 0 100%); 
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
                    border-radius: 0 0 20px 20px;
                }
                .partner-header h1 {
                    font-size: 3rem;
                    margin-bottom: 0.5rem;
                }
                .partner-header p {
                    font-size: 1.1rem;
                    opacity: 0.9;
                }

                /* General Content Sections */
                .content-section {
                    background-color: var(--bg-white);
                    padding: 2.5rem;
                    margin-bottom: 2.5rem;
                    border-radius: 16px;
                    box-shadow: 0 8px 25px var(--shadow-light);
                    animation: slide-in 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94); 
                    border-top: 5px solid var(--sky-orange);
                    transition: box-shadow 0.5s;
                }
                .content-section:hover {
                    box-shadow: 0 10px 30px rgba(0, 71, 171, 0.25);
                }
                .content-section h2 {
                    font-size: 2rem;
                    color: var(--deep-blue);
                    border-bottom: 3px solid var(--sky-blue);
                    padding-bottom: 0.5rem;
                    margin-bottom: 1.5rem;
                }

                /* Feature Cards with Hover/3D Animation */
                .feature-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }
                .feature-list li {
                    padding: 0.5rem 0;
                    border-bottom: 1px dashed #eee;
                    font-size: 1rem;
                    color: var(--text-dark);
                }
                .feature-list li .icon {
                    color: var(--sky-orange);
                    margin-right: 0.5rem;
                    font-weight: bold;
                }

                .feature-list.grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 1.5rem;
                }
                .feature-list.grid li {
                    background-color: #f7faff;
                    border-radius: 12px;
                    padding: 1.5rem;
                    border-left: 5px solid var(--sky-blue);
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
                    transition: transform 0.4s ease-out, box-shadow 0.4s, border-color 0.4s;
                    transform-style: preserve-3d; 
                    position: relative;
                    overflow: hidden;
                    border-bottom: none; /* Override general list styling */
                }

                /* Hover Effect: 3D Tilt */
                .feature-list.grid li:hover {
                    transform: translateY(-5px) rotateX(2deg) rotateY(2deg);
                    box-shadow: 0 15px 30px rgba(0, 71, 171, 0.2);
                    border-left-color: var(--sky-orange);
                }

                /* SVG Icon Styling and VFX */
                .feature-list.grid li .icon-wrapper {
                    margin-bottom: 1rem;
                    position: relative;
                    width: 50px;
                    height: 50px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--sky-blue);
                    border-radius: 50%;
                    color: var(--bg-white);
                    transition: transform 0.5s;
                    animation: vfx-pulse 2s infinite; 
                }

                /* Icon Rotation on Hover */
                .feature-list.grid li:hover .icon-wrapper {
                    transform: scale(1.1) rotate(15deg); 
                }

                .icon-wrapper svg {
                    width: 60%;
                    height: 60%;
                    fill: var(--bg-white);
                    transition: fill 0.3s;
                }


                /* Duct Factory Unit Section */
                .duct-factory-section {
                    border-top: 5px solid var(--deep-blue);
                    animation: slide-in 1s ease-out 0.2s; 
                }
                .duct-factory-content {
                    display: flex;
                    gap: 2rem;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }
                .duct-factory-image img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 10px;
                    transition: transform 0.5s ease-in-out;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                }
                .duct-factory-image:hover img {
                    transform: scale(1.05) rotate(1deg); 
                }

                /* Technology Section Specifics */
                .technology-section h2 {
                    color: var(--sky-orange);
                    border-bottom-color: var(--light-orange);
                }
                .technology-section .tech-item {
                    padding: 1rem;
                    background-color: #fff8f5;
                    border-radius: 8px;
                    margin-bottom: 1rem;
                    border-left: 4px solid var(--sky-orange);
                }
                
                /* Media Grid */
                .media-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 1rem;
                    margin-top: 2rem;
                }
                .media-item img, .media-item.video {
                    width: 100%;
                    height: 150px;
                    object-fit: cover;
                    border-radius: 8px;
                    display: block;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                }
                .media-item.video {
                    background-color: #ddd;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: var(--text-dark);
                    text-align: center;
                    font-size: 1rem;
                }

                /* Call to Action */
                .partner-cta {
                    text-align: center;
                    background-color: var(--deep-blue);
                    color: var(--bg-white);
                    padding: 3rem;
                    border-radius: 16px;
                    margin-top: 3rem;
                    box-shadow: 0 10px 30px rgba(0, 71, 171, 0.4);
                }
                .partner-cta h3 {
                    font-size: 2rem;
                    margin-bottom: 1rem;
                }
                .partner-cta .contact-btn {
                    background-color: var(--sky-orange);
                    color: var(--bg-white);
                    border: none;
                    padding: 0.8rem 2rem;
                    font-size: 1.1rem;
                    font-weight: bold;
                    border-radius: 50px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    margin-top: 1rem;
                    box-shadow: 0 5px 15px rgba(255, 69, 0, 0.4);
                    position: relative;
                    z-index: 10;
                }
                .partner-cta .contact-btn:hover {
                    background-color: var(--light-orange);
                    transform: translateY(-2px) scale(1.02);
                    box-shadow: 0 8px 20px rgba(255, 69, 0, 0.6);
                }


                /* Media Queries */
                @media (max-width: 900px) {
                    .duct-factory-content {
                        flex-direction: column;
                    }
                    .duct-factory-image {
                        max-width: 250px;
                        margin-bottom: 1.5rem;
                    }
                    .partner-header h1 {
                        font-size: 2.2rem;
                    }
                    .content-section {
                        padding: 1.5rem;
                    }
                    .feature-list.grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
            <>
            <Header />
            <div className="partner-page">
                {/* Header */}
                <header className="partner-header">
                    <h1>
                        Strategic Partnership with <SparkleText>Sky Breeze</SparkleText>
                    </h1>
                    <p>Scaling your success with reliable HVAC engineering, service, and custom fabrication.</p>
                </header>

                {/* Who We Are Section (Content Updated) */}
                <section className="content-section">
                    <h2>Who We Are: The Execution Engine</h2>
                    <div className="duct-factory-content">
                        <div style={{ flex: 1 }}>
                            <p>We are more than just service providers; we are your strategic execution partner. With a decade of dedicated service in the HVAC industry, Sky Breeze has managed complex installations for large infrastructure projects, including high-footfall hotels, hospitals, and major commercial complexes across India. Our BNI membership further strengthens our commitment to professional network excellence and reliable delivery.</p>
                            <p>Our core strength lies in our **specialized, multi-brand authorization** and our commitment to using the highest-grade materials and certified processes, ensuring every installation meets or exceeds international standards.</p>
                        </div>
                        {/* Sky Breeze Logo */}
                        <div className="duct-factory-image" style={{ maxWidth: '100px', alignSelf: 'center' }}>
                            <img 
                                src={logo} 
                                alt="Sky Breeze AC Solutions Logo" 
                                onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/200x200/CCCCCC/000000?text=SkyBreeze+Logo"; }}
                            />
                        </div>
                    </div>
                </section>

                {/* Core Services Section (Updated) */}
                <section className="content-section">
                    <h2>🛠️ End-to-End Solutions</h2>
                    <p>Our comprehensive service portfolio is designed to support the full lifecycle of your project, from design and fabrication to installation and maintenance.</p>
                    <ul className="feature-list">
                        {coreServices.map((service, index) => (
                            <li key={index}><span className="icon">✓</span> {service}</li>
                        ))}
                    </ul>
                </section>

                {/* Why Choose Sky Breeze Section (Enhanced with Icons and Animation) */}
                <section className="content-section">
                    <h2>🌟 Partnership Advantages (The Sky Breeze Difference)</h2>
                    <ul className="feature-list grid">
                        {whyChooseUsFeatures.map((feature, index) => (
                            <FeatureCard 
                                key={index}
                                title={feature.title} 
                                description={feature.description} 
                                iconName={feature.iconName}
                            />
                        ))}
                    </ul>
                </section>
                
                {/* NEW SECTION: Technology & Innovation */}
                <section className="content-section technology-section">
                    <h2>📈 Technology & Innovation in HVAC</h2>
                    <p>We don't just install; we optimize. Our commitment to incorporating the latest technology ensures your projects are future-proofed and energy-efficient.</p>
                    <div className="feature-list grid">
                        {technologyFeatures.map((tech, index) => (
                            <div key={index} className="tech-item">
                                <h4 style={{ color: 'var(--deep-blue)', fontWeight: 600, marginBottom: '0.5rem' }}>{tech.title}</h4>
                                <p style={{ fontSize: '0.95rem' }}>{tech.detail}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Duct Factory Unit Section */}
                <section className="content-section duct-factory-section">
                    <h2>🏭 In-House Duct Manufacturing Unit (Quality Controlled)</h2>
                    <div className="duct-factory-content">
                        <div style={{ flex: 1 }}>
                            <p>Our dedicated facility guarantees precision fabrication for all ducting needs. We ensure timely delivery of custom-designed spiral, rectangular, and special-shape ducts that meet stringent quality and air-tightness standards, giving you direct control over your project timeline and material quality.</p>

                            <p style={{ marginTop: '1rem', fontStyle: 'italic', color: 'var(--deep-blue)' }}>Key Benefits: **Zero lead-time delays** and **100% material traceability**.</p>
                        </div>
                    </div>
                    
                    {/* Media Grid for Duct Factory and Technicians */}
                    <div className="media-grid">
                        {mediaAssets.map((item, index) => (
                            <div key={index} className="media-item">
                                {item.type === 'image' ? (
                                    <img 
                                        src={item.src} 
                                        alt={item.alt} 
                                        onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x150/CCCCCC/000000?text=AC+Work+Image"; }}
                                    />
                                ) : (
                                    <div className="media-item video">
                                        <span style={{fontSize: '3rem'}}>🎥</span>
                                        {item.src}
                                        <small>(Placeholder for video content)</small>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>


                {/* Call to Action */}
                <section className="partner-cta">
                    <h3>Ready to Accelerate Your Next Project?</h3>
                    <p>Connect with our Project Management team today to discuss tailored solutions for your commercial, industrial, or residential development.</p>
                    <button className="contact-btn" onClick={handleCtaClick}>
                        Start the Partnership Conversation
                    </button>
                </section>
            </div>
            <Footer />
            
            </>
        </>
    );
}